import React from 'react';
import { Badge } from '../../components/ui/Badge/Badge';
import { Avatar } from '../../components/ui/Avatar/Avatar';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { apiFetch } from '@/lib/api';

export function UsersList() {
  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [totalItems, setTotalItems] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(15);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
        ...(searchTerm && { search: searchTerm }),
        ...filters,
        ...(sortConfig && { 
          sortBy: sortConfig.key,
          sortOrder: sortConfig.direction
        })
      });

      const res = await apiFetch(`/api/admin/users?${params}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load');
      
      setUsers(json.data || []);
      setTotalItems(json.pagination?.total || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, searchTerm, filters, sortConfig]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const columns = [
    {
      key: 'user',
      title: 'User',
      render: (_: any, record: any) => (
        <div className="flex items-center gap-3">
          <Avatar
            size="sm"
            initials={record.full_name ? record.full_name.charAt(0).toUpperCase() : record.email.charAt(0).toUpperCase()}
            name={record.full_name || record.email}
          />
          <div>
            <div className="font-medium text-gray-900">{record.email}</div>
            <div className="text-sm text-gray-500">{record.full_name || 'No name provided'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'is_email_verified',
      title: 'Email Status',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'success' : 'warning'}>
          {value ? 'Verified' : 'Unverified'}
        </Badge>
      )
    },
    {
      key: 'is_super_admin',
      title: 'Role',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'secondary' : 'outline'}>
          {value ? 'Super Admin' : 'User'}
        </Badge>
      )
    },
    {
      key: 'created_at',
      title: 'Registered',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    }
  ];

  const filterOptions = [
    {
      key: 'is_email_verified',
      label: 'Email Status',
      options: [
        { label: 'Verified', value: 'true' },
        { label: 'Unverified', value: 'false' }
      ]
    },
    {
      key: 'is_super_admin',
      label: 'Role',
      options: [
        { label: 'Super Admin', value: 'true' },
        { label: 'Regular User', value: 'false' }
      ]
    }
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Users"
        description="Manage all users on the platform"
      />
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={users}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search users by email or name..."
        filters={filterOptions}
        pagination={{
          pageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 25, 50]
        }}
        emptyText="No users found."
        serverSide={true}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onSort={handleSort}
        onPaginate={handlePaginate}
        totalItems={totalItems}
      />
    </PageContainer>
  );
}


